class DeliveryDateCalculator {
  constructor() {
    this.deliveryTimeInfo = document.querySelector('.delivery-time-info');
    this.dateInput = document.querySelector('#delivery-date');
    this.errorEl = document.querySelector('#delivery-date-error');
    this.form = this.dateInput ? this.dateInput.closest('form') : null;
    this.hiddenAttrInput = document.querySelector('#product-form-attribute-delivery-date');

    if (this.dateInput) {
      this.init();
    }
  }

  init() {
    const earliestDate = this.calculateEarliestDeliveryDate();
    this.earliestDate = earliestDate;

    // Set min attribute to prevent past dates in native picker
    this.dateInput.min = this.formatDateForInput(earliestDate);

    // Show delivery countdown / earliest info
    this.showDeliveryInfo(earliestDate);

    // Pre-fill from existing cart attribute if present, without auto-filling an arbitrary date
    if (!this.dateInput.value) {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.json())
        .then(cart => {
          if (cart.attributes && cart.attributes.Lieferdatum && !this.dateInput.value) {
            const parts = cart.attributes.Lieferdatum.split('.');
            if (parts.length === 3) {
              const parsedVal = `${parts[2]}-${parts[1]}-${parts[0]}`;
              this.dateInput.value = parsedVal;
              if (this.hiddenAttrInput) {
                this.hiddenAttrInput.value = cart.attributes.Lieferdatum;
              }
            }
          }
        })
        .catch(() => {});
    } else {
      this.validateSelectedDate(false);
    }

    this.dateInput.addEventListener('change', () => {
      this.validateSelectedDate(true);
    });

    this.dateInput.addEventListener('input', () => {
      if (this.dateInput.value) {
        this.validateSelectedDate(false);
      }
    });

    // Intercept form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        if (!this.validateSelectedDate(true)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const atcBtn = this.form.querySelector('[data-add-to-cart], [name="add"], .add-to-cart');
          if (atcBtn) atcBtn.classList.remove('btn--loading');
          return false;
        }
      }, true);

      // Also intercept button clicks (ATC, dynamic checkout buttons)
      const buttons = this.form.querySelectorAll(
        '[data-add-to-cart], [name="add"], .add-to-cart, .shopify-payment-button, [data-shopify="payment-button"]'
      );
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          if (!this.validateSelectedDate(true)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            btn.classList.remove('btn--loading');
            return false;
          }
        }, true);
      });
    }
  }

  syncCartAttribute(formattedDate) {
    if (!formattedDate) return Promise.resolve();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Lieferdatum': formattedDate } }),
      credentials: 'same-origin'
    }).catch(function() {});
  }

  calculateEarliestDeliveryDate() {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    let deliveryDate = new Date(now);
    deliveryDate.setHours(0, 0, 0, 0);

    // Cutoff: 17:00 for next-day delivery (Mon-Sun, including Sundays)
    if (currentHour > 17 || (currentHour === 17 && currentMinute > 0)) {
      deliveryDate.setDate(deliveryDate.getDate() + 2);
    } else {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }

    // Skip Saturdays — store is open Sundays in Frankfurt & Düsseldorf
    deliveryDate = this.skipDay(deliveryDate, 6);

    return deliveryDate;
  }

  skipDay(date, dayToSkip) {
    if (date.getDay() === dayToSkip) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  showDeliveryInfo(earliestDate) {
    if (!this.deliveryTimeInfo) return;

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const formattedDate = this.formatDateLong(earliestDate);

    let message = '';

    if (currentHour < 17) {
      const hoursLeft = 17 - currentHour;
      const minutesLeft = 60 - currentMinute;
      let timeText = `${hoursLeft} Std`;
      if (hoursLeft === 0) {
        timeText = `${minutesLeft} Min`;
      }
      message = `⏰ <strong>Bestellen Sie in den nächsten ${timeText}</strong> → Früheste Lieferung am <strong>${formattedDate}</strong>`;
    } else {
      message = `⏰ Bestellungen nach 17 Uhr → Früheste Lieferung am <strong>${formattedDate}</strong>`;
    }

    this.deliveryTimeInfo.innerHTML = message;
    this.deliveryTimeInfo.style.display = 'block';
  }

  showInlineError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.style.display = 'block';
    }
  }

  hideInlineError() {
    if (this.errorEl) {
      this.errorEl.style.display = 'none';
    }
  }

  validateSelectedDate(showErrors = false) {
    this.hideInlineError();
    if (this.dateInput) {
      this.dateInput.style.borderColor = '#d1d5db';
    }

    const value = this.dateInput ? this.dateInput.value : '';
    if (!value) {
      if (showErrors) {
        this.showInlineError('Bitte wählen Sie ein Lieferdatum für Ihre Bestellung aus.');
        if (this.dateInput) {
          this.dateInput.style.borderColor = '#c2185b';
          this.dateInput.focus();
        }
      }
      return false;
    }

    const parsed = new Date(value + 'T00:00:00');
    if (isNaN(parsed.getTime())) {
      if (showErrors) {
        this.showInlineError('Bitte wählen Sie ein gültiges Datum aus dem Kalender.');
        if (this.dateInput) {
          this.dateInput.style.borderColor = '#c2185b';
          this.dateInput.focus();
        }
      }
      return false;
    }

    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (parsed < today) {
      if (showErrors) {
        this.showInlineError('Das gewählte Datum liegt in der Vergangenheit. Bitte wählen Sie ein zukünftiges Datum.');
        if (this.dateInput) {
          this.dateInput.style.borderColor = '#c2185b';
          this.dateInput.focus();
        }
      }
      return false;
    }

    // Check if date is before earliest delivery date
    if (parsed < this.earliestDate) {
      if (showErrors) {
        this.showInlineError(`Früheste Lieferung: ${this.formatDateLong(this.earliestDate)}. Bitte wählen Sie dieses oder ein späteres Datum.`);
        if (this.dateInput) {
          this.dateInput.style.borderColor = '#c2185b';
          this.dateInput.focus();
        }
      }
      return false;
    }

    // Warn about Saturdays (store delivers Sundays)
    if (parsed.getDay() === 6) {
      this.showInlineError('Samstaglieferung ist leider nicht verfügbar. Wir liefern gerne am Sonntag.');
    }

    // Valid date — sync cart attribute
    const formattedDate = this.formatDateGerman(parsed);
    if (this.hiddenAttrInput) {
      this.hiddenAttrInput.value = formattedDate;
    }
    this.syncCartAttribute(formattedDate);
    return true;
  }

  formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateGerman(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}.${month}.${year}`;
  }

  formatDateLong(date) {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('de-DE', options);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DeliveryDateCalculator();
  });
} else {
  new DeliveryDateCalculator();
}
